import React from 'react';
import { useSelector } from 'react-redux';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';

import SectionHeader from '../SectionHeader';
import OptimizedImage from '../OptimizedImage';
import PlayWhite from '../../Icons/PlayWhite';
import PauseIconSmall from '../../Icons/PauseIconSmall';
import {
  radioPlayerPlayVod,
  radioPlayerPause,
} from '../../../utils/radioPlayerEvents';

import type { PodcastEpisodesSectionProps } from './PodcastEpisodesSection.types';

function PodcastEpisodeSkeletonCard({ index }: { index: number }) {
  return (
    <div
      key={index}
      role="listitem"
      className="podcast-episodes-section__item podcast-episodes-section__item--loading"
    >
      <article className="podcast-card podcast-card--loading">
        <div className="podcast-card__mediaWrap">
          <div className="podcast-card__media">
            <div className="podcast-episodes-section__loadingImage shimmer" />
            <div className="podcast-card__shade" aria-hidden="true" />
          </div>

          <div className="podcast-episodes-section__loadingFloating shimmer" />
        </div>

        <div className="podcast-card__body">
          <div className="podcast-card__metaRow">
            <div className="podcast-episodes-section__loadingBadge shimmer" />
            <div className="podcast-episodes-section__loadingMeta shimmer" />
          </div>

          <div className="podcast-episodes-section__loadingTitle shimmer" />
        </div>
      </article>
    </div>
  );
}

export default function PodcastEpisodesSection({
  title,
  subtitle = '',
  href,
  actionLabel = 'Ver todos',
  items,
  loading = false,
  emptyMessage = 'Nenhum episódio de podcast encontrado',
  className = '',
}: PodcastEpisodesSectionProps) {
  const playerMode = useSelector((state: any) => state.player?.mode ?? '');
  const isPlaying = useSelector((state: any) => !!state.player?.isPlaying);
  const currentVodUrl = useSelector((state: any) => state.player?.vodUrl || '');

  return (
    <section
      className={['podcast-episodes-section', className]
        .filter(Boolean)
        .join(' ')}
    >
      <SectionHeader
        title={title}
        subtitle={subtitle}
        href={href}
        actionLabel={actionLabel}
      />

      {loading ? (
        <div
          className="podcast-episodes-section__grid podcast-episodes-section__grid--loading"
          role="list"
          aria-label="Carregando episódios de podcast"
        >
          {Array.from({ length: 7 }).map((_, index) => (
            <PodcastEpisodeSkeletonCard key={index} index={index} />
          ))}
        </div>
      ) : null}

      {!loading && items.length ? (
        <div
          className="podcast-episodes-section__grid"
          role="list"
          aria-label="Lista de episódios de podcast"
        >
          {items.map((item) => {
            const vodUrl = item.audio || '';

            const isPlayingThis =
              !!vodUrl &&
              playerMode === 'vod' &&
              isPlaying &&
              currentVodUrl === vodUrl;

            const handleAudioClick = (
              event: React.MouseEvent<HTMLButtonElement>,
            ) => {
              event.preventDefault();
              event.stopPropagation();

              if (!vodUrl) return;

              if (isPlayingThis) {
                radioPlayerPause();
                return;
              }

              radioPlayerPlayVod(
                vodUrl,
                item.title,
                item.programTitle,
                item.image,
                item.pageUrl || item['@id'],
                {
                  origin: 'PodcastEpisodesSection',
                  analytics: {
                    event: 'audio_play',
                    section: 'home.podcasts',
                    label: item.title,
                    contentType: 'vod',
                  },
                },
              );
            };

            const itemAriaLabel = item.programTitle
              ? `${item.title}, podcast do programa ${item.programTitle}`
              : `${item.title}, podcast`;

            return (
              <div
                key={item['@id']}
                role="listitem"
                className="podcast-episodes-section__item"
              >
                <article
                  className={
                    isPlayingThis
                      ? 'podcast-card podcast-card--playing'
                      : 'podcast-card'
                  }
                >
                  <div className="podcast-card__mediaWrap">
                    <UniversalLink
                      href={item['@id']}
                      className="podcast-card__mediaLink pressable"
                      aria-label={itemAriaLabel}
                    >
                      <div className="podcast-card__media">
                        {item.image ? (
                          <OptimizedImage
                            src={item.image}
                            imageVersion={item.imageVersion}
                            sizes="(max-width: 767px) 108px, (max-width: 991px) 25vw, 220px"
                            alt=""
                            loading="lazy"
                            decoding="async"
                            width="400"
                            height="400"
                          />
                        ) : (
                          <div
                            className="podcast-card__placeholder"
                            aria-hidden="true"
                          >
                            <span className="podcast-card__placeholderText">
                              Podcast
                            </span>
                          </div>
                        )}

                        <div
                          className="podcast-card__shade"
                          aria-hidden="true"
                        />
                        {isPlayingThis ? (
                          <div className="podcast-card__nowPlaying">
                            <span
                              className="podcast-card__equalizer"
                              aria-hidden="true"
                            >
                              <i />
                              <i />
                              <i />
                            </span>
                            <span>Tocando</span>
                          </div>
                        ) : null}
                      </div>
                    </UniversalLink>

                    {vodUrl ? (
                      <button
                        type="button"
                        className={`podcast-card__floating ${
                          isPlayingThis ? 'is-playing' : ''
                        }`}
                        aria-label={
                          isPlayingThis ? 'Pausar áudio' : 'Reproduzir áudio'
                        }
                        aria-pressed={isPlayingThis}
                        onClick={handleAudioClick}
                      >
                        <span className="podcast-card__play">
                          {isPlayingThis ? (
                            <PauseIconSmall className="podcast-card__playWhiteIcon" />
                          ) : (
                            <PlayWhite className="podcast-card__playWhiteIcon" />
                          )}
                        </span>
                      </button>
                    ) : null}
                  </div>

                  <UniversalLink
                    href={item['@id']}
                    className="podcast-card__bodyLink"
                    aria-label={itemAriaLabel}
                  >
                    <div className="podcast-card__body">
                      <div className="podcast-card__metaRow">
                        <div className="podcast-card__metaBadge">Podcast</div>

                        {item.programTitle ? (
                          <div className="podcast-card__metaText">
                            {item.programTitle}
                          </div>
                        ) : null}
                      </div>

                      <h3 className="podcast-card__title">{item.title}</h3>
                    </div>
                  </UniversalLink>
                </article>
              </div>
            );
          })}
        </div>
      ) : null}

      {!loading && !items.length ? (
        <div className="podcast-episodes-section__state">{emptyMessage}</div>
      ) : null}
    </section>
  );
}
